<template>
  <div v-if="error" class="alert alert-danger">
    <h5 class="alert-heading">{{ $t("js.backend.voter_importer.error") }}</h5>
    <details>
      <summary v-text="error.message"/>
      <pre v-text="error.backtrace.join('\n')"/>
    </details>
  </div>
  <div v-else-if="doneAt">
    {{ $t("js.backend.components.contest_importer.done") }}
  </div>
  <div v-else>
    <p>
      <time :datetime="startedAt" v-if="startedAt">{{ $t("js.backend.voter_importer.started_time_ago", {time_ago: startedAgo}) }}</time>
      <span v-else>{{ $t("js.backend.voter_importer.starting_soon") }}</span>
    </p>
    <div class="progress mb-5">
        <div class="progress-bar" role="progressbar" :aria-valuenow="progressCurrent" aria-valuemin="0" :aria-valuemax="progressTotal" :style="{width: progressPercentage}">
          {{ `${progressCurrent}/${progressTotal}` }}
        </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onBeforeMount, onMounted, onBeforeUnmount } from "vue";
import { ContestImporterChannelSubscription } from "@/channels/ContestImporterChannel";
import { useI18n } from "vue-i18n";
import type { ContestImporter, ContestImporterInitialData, PropType, Locale } from "@/types";

const props = defineProps({
  importerId: {
    type: Number,
    required: true,
  },
  initialProgress: {
    type: Object as PropType<ContestImporterInitialData>,
    required: true,
  },
  initialWorkflowState: {
    type: String,
    required: true,
  },
});

const i18n = useI18n();

const relativeTimeFormatter = new Intl.RelativeTimeFormat((i18n.locale.value || "da") as Locale, { numeric: "auto" });

const progressCurrent = ref(props.initialProgress.current);

const progressTotal = ref(props.initialProgress.total);

const reloading = ref(false);

const intervalId = ref(null);

const startedAgo = ref(null);

const error = ref(null);

const subscription = ref<ContestImporterChannelSubscription | null>(null);

const startedAt = ref(null);

const doneAt = ref(null);

const progressPercentage = computed(() => `${Math.round(100 * progressCurrent.value / progressTotal.value)}%`);

const startedAtDateTime = computed(() => Date.parse(startedAt.value));

const updateTimestamp = () => {
  if (!startedAtDateTime.value) return;
  const value = Math.round((startedAtDateTime.value - Date.now()) / 1000);
  if (Math.abs(value) >= 60) {
    startedAgo.value = relativeTimeFormatter.format(Math.floor(value / 60), "minute");
  } else {
    startedAgo.value = relativeTimeFormatter.format(value, "second");
  }
};

const received = (data: ContestImporter) => {
  const { progress_current, progress_total, workflow_state, job_started_at, job_done_at, job_last_error } = data;
  progressCurrent.value = progress_current;
  progressTotal.value = progress_total;
  startedAt.value = job_started_at;
  doneAt.value = job_done_at;
  error.value = job_last_error;
  if (props.initialWorkflowState !== workflow_state) reloadPage();
};

const reloadPage = () => {
  if (reloading.value) return;
  reloading.value = true;
  setTimeout(() => window.location.reload(), 500); // Small delay, so the user can see the progressbar hit 100%
};

onBeforeMount(() => {
  subscription.value = new ContestImporterChannelSubscription(props.importerId, {
    received: received,
  });
});

onMounted(() => {
  updateTimestamp();
  intervalId.value = setInterval(updateTimestamp, 1000);
});

onBeforeUnmount(() => {
  clearInterval(intervalId.value);
  subscription.value?.unsubscribe();
});
</script>
